import type {
  MealPlanResponse,
  PlanningProfile,
  PlanningTable,
} from '@/api/use-get-meal-plan';
import type {
  SaveMealPlanRecommendation,
  SaveMealPlanRequestBody,
} from '@/api/use-save-meal-plan';
import type {
  AIPlannerPreferences,
  MealPlan,
  PlannedMeal,
  RecommendationMealItem,
  StreamingMealItem,
  WeekDay,
} from '@/store/ai-planner';

/**
 * Convert streaming recommendations to SaveMealPlanRequestBody format
 */
export function convertRecommendationsToSaveRequest(
  recommendations: RecommendationMealItem[],
  preferences: Partial<AIPlannerPreferences>,
  cityId?: string,
  address?: { lat?: number; lng?: number }
): SaveMealPlanRequestBody {
  const planningProfile: PlanningProfile = {
    city_id: cityId ?? '',
    prefer_time_slot: ['lunch'],
    budget: preferences.budget ?? 0,
    max_price: preferences.budget ?? 0,
    max_distance_km: 10,
    allergies: preferences.allergies?.map((a) => a.toString()) ?? [],
    dietary_goals: preferences.eatingGoals?.join(', ') ?? '',
    dietary_preference: preferences.dietType ?? '',
    name: '',
    lat: address?.lat ?? 0,
    lng: address?.lng ?? 0,
    user_note: preferences.userNote ?? '',
  };

  const saveMealPlanRecommendations: SaveMealPlanRecommendation[] =
    recommendations.map((item) => ({
      date: item.date,
      slot: item.slot,
      planning_profile: planningProfile,
      category_solo_meal: item.category_solo_meal,
      overall_dish_tag: item.overall_dish_tag,
      estimated_price: item.estimated_price,
      image_thumb_urls: item.image_thumb_urls,
      name: item.name,
    }));

  return {
    recommendations: saveMealPlanRecommendations,
    planning_profile: planningProfile,
  };
}

/**
 * Convert MealPlanResponse from database to MealPlan store format
 */
export function convertMealPlanResponseToStore(
  response: MealPlanResponse,
  preferences: Partial<AIPlannerPreferences>
): MealPlan {
  // Group planning table by date
  const mealsByDate: Record<string, PlanningTable[]> = {};

  response.planning_table.forEach((item) => {
    if (!mealsByDate[item.date]) {
      mealsByDate[item.date] = [];
    }
    mealsByDate[item.date].push(item);
  });

  // Convert to DayPlan format
  const getWeekDay = (dateStr: string): WeekDay => {
    const date = new Date(dateStr);
    const dayOfWeek = date.getDay();
    const dayMap: Record<number, WeekDay> = {
      1: 'T2',
      2: 'T3',
      3: 'T4',
      4: 'T5',
      5: 'T6',
    };
    return dayMap[dayOfWeek] ?? 'T2';
  };

  const days = Object.entries(mealsByDate)
    .sort(([a], [b]) => new Date(a).getTime() - new Date(b).getTime())
    .map(([date, dateMeals]) => ({
      date,
      dayOfWeek: getWeekDay(date),
      meals: dateMeals.map(
        (meal): PlannedMeal => ({
          id: `meal-${meal.item_name}-${date}-${meal.slot}`,
          name: meal.item_name,
          imageUrl: meal.image_thumb_urls,
          priceMin: meal.estimated_price,
          priceMax: meal.estimated_price,
          deliveryTime: meal.date,
          status:
            meal.status === 'completed'
              ? 'passed'
              : meal.status === 'skipped'
                ? 'cancelled'
                : 'upcoming',
          aiReason: `${meal.overall_dish_tag} - ${meal.category_solo_meal?.join(', ')}`,
          overallDishTag: meal.overall_dish_tag,
          estimatedPrice: meal.estimated_price,
          budget: meal.budget ?? meal.planning_profile?.budget,
          user_note: meal.user_note ?? meal.planning_profile?.user_note,
          slot: meal.slot,
        })
      ),
    }));

  // Calculate date range
  const dates = Object.keys(mealsByDate).sort();
  const startDate = dates[0] ?? new Date().toISOString();
  const endDate = dates[dates.length - 1] ?? new Date().toISOString();

  return {
    id: response.id,
    createdAt: new Date(response.create_at).toISOString(),
    preferences: preferences as AIPlannerPreferences,
    weeks: [
      {
        weekNumber: 1,
        startDate,
        endDate,
        days,
      },
    ],
  };
}

/**
 * Convert StreamingMealItem array to RecommendationMealItem format
 */
export function convertStreamingMealsToRecommendations(
  streamingMeals: StreamingMealItem[]
): RecommendationMealItem[] {
  return streamingMeals.map((meal) => ({
    date: meal.date,
    slot: 'lunch', // Default to lunch, adjust based on delivery_time if needed
    plan_id: '',
    planning_profile: {
      dietary_goals: '',
      dietary_preference: '',
      allergies: [],
      user_note: '',
    },
    category_solo_meal: [],
    overall_dish_tag: meal.ai_reason?.split(' - ')[0] ?? '',
    estimated_price: meal.price_min,
    image_thumb_urls: meal.image_url,
    name: meal.name,
  }));
}
